import React, { useEffect, useMemo, useRef } from 'react';
import moment from 'moment-timezone';
import { Box, List, ListSubheader, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useTranslation } from 'services/localization/useTranslation';
import {
	AvailabilityByTime,
	isTimeSlotWithAvailability,
	ValidSlotsByTime,
} from 'common/modules/availabilities/timeSlots/types';
import { Duration, OpeningHoursWithExceptions, OpeningHourTimesWithMoments } from 'common/types';
import {
	StartTimeObject,
	TimeSelectorArgs,
	TimeSelectorStyling,
} from 'components/TimeSelector/types';
import TimeSlot from './TimeSlot';

interface StartTimesByType {
	startTimesClosed: StartTimeObject[];
	startTimesBeforeOpen: StartTimeObject[];
	startTimesAfterClose: StartTimeObject[];
	startTimesWithinHours: StartTimeObject[];
}

interface Props {
	existingDateSelection: string;
	onSelectDate: (data: moment.Moment) => void;
	timeSlots: AvailabilityByTime | ValidSlotsByTime;
	openingHours: OpeningHoursWithExceptions;
	openingTimes: OpeningHourTimesWithMoments | null;
	selectorType: TimeSelectorArgs;
	showTimesOutsideOpeningHours: boolean;
	loading: boolean;
	showAvailability: boolean;
	duration: Duration;
	styling?: TimeSelectorStyling;
	minDate?: moment.Moment;
}
const TimeSlots = (props: Props) => {
	const classes = useStyles();
	const {
		existingDateSelection,
		onSelectDate,
		timeSlots,
		openingHours,
		openingTimes,
		selectorType,
		showTimesOutsideOpeningHours,
		loading,
		showAvailability,
		duration,
		styling,
		minDate,
	} = props;
	const { t } = useTranslation();
	const selectedItemRef = useRef<HTMLDivElement>(null);
	const dateMoment = moment(existingDateSelection);
	const dateString = dateMoment.format('YYYY-MM-DD');
	const startTimes = Object.keys(timeSlots[dateString] ?? {});
	const isClosed = !openingTimes || openingTimes.isClosed;
	const isEndDate = selectorType.type === 'endTime';
	const mappedStartTimes: StartTimeObject[] = startTimes.map((hourString) => {
		const [hours, minutes] = hourString.split(':');
		const time = moment(dateString).hour(Number(hours)).minutes(Number(minutes)).startOf('minute');
		return {
			time,
			diffToStart:
				selectorType.type === 'endTime' ? time.diff(selectorType.startTime, 'seconds') : null,
			beforeOpening: !!openingTimes && !openingTimes.isClosed && hourString < openingTimes.openTime,
			afterClosing: !openingTimes ? false : hourString > openingTimes.closeTime,
			isClosed: isClosed,
		};
	});

	const filteredStartTimes = mappedStartTimes.filter((startTime) => {
		if (startTime.diffToStart !== null && startTime.diffToStart <= 0) return false;
		if (minDate && startTime.time.isBefore(minDate)) return false;
		return true;
	});

	const startTimesByType: StartTimesByType = filteredStartTimes.reduce(
		(result, startTime) => {
			if (startTime.isClosed) {
				result.startTimesClosed.push(startTime);
			} else if (startTime.beforeOpening) {
				result.startTimesBeforeOpen.push(startTime);
			} else if (startTime.afterClosing) {
				result.startTimesAfterClose.push(startTime);
			} else {
				result.startTimesWithinHours.push(startTime);
			}
			return result;
		},
		{
			startTimesClosed: [],
			startTimesBeforeOpen: [],
			startTimesAfterClose: [],
			startTimesWithinHours: [],
		} as StartTimesByType,
	);

	const allVisibleStartTimes = useMemo(() => {
		if (showTimesOutsideOpeningHours) {
			return filteredStartTimes;
		}
		return startTimesByType.startTimesWithinHours;
	}, [filteredStartTimes, startTimesByType, showTimesOutsideOpeningHours]);

	const selectedStartTime = useMemo(() => {
		return allVisibleStartTimes.find((t) => moment(existingDateSelection).isSame(t.time));
	}, [existingDateSelection, allVisibleStartTimes]);

	useEffect(() => {
		if (!!selectedItemRef?.current) {
			setTimeout(() => {
				selectedItemRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}, 10);
		}
	}, [selectedStartTime]);

	const renderTimeSlot = (startTime: StartTimeObject) => {
		const isSelected = dateMoment.isSame(startTime.time) ?? false;
		const day = startTime.time.format('YYYY-MM-DD');
		const hour = startTime.time.format('HH:mm');
		const validity = timeSlots[day][hour];
		const isInvalid = !validity || (isTimeSlotWithAvailability(validity) && validity.units < 0);
		return (
			<TimeSlot
				key={startTime.time.valueOf()}
				innerRef={isSelected ? selectedItemRef : null}
				timeSlotObject={startTime}
				isSelected={isSelected}
				onSelect={(newTime: moment.Moment) => onSelectDate(newTime)}
				isInvalid={isInvalid}
				isClosed={isClosed}
				selectorType={selectorType}
				loading={loading}
				showAvailability={showAvailability}
				duration={duration}
				openingHours={openingHours}
				styling={styling}
			/>
		);
	};

	if (filteredStartTimes.length === 0) {
		return (
			<Box
				flex="1"
				display="flex"
				flexDirection="column"
				justifyContent="center"
				alignItems="center"
				p={4}
			>
				<Typography gutterBottom variant="body1">
					{!isEndDate
						? t('timeLocationSelector.noSelectableStartTimes', 'No selectable start times')
						: t('timeLocationSelector.noSelectableEndTimes', 'No selectable end times')}
				</Typography>
				<Typography variant="caption">
					{t('timeLocationSelector.pleaseChooseDifferentDay', 'Please choose different day')}
				</Typography>
			</Box>
		);
	}

	if (!!startTimesByType.startTimesClosed.length) {
		return (
			<List dense className={classes.startTimesList} subheader={<li />}>
				{startTimesByType.startTimesClosed.map((startTime) => {
					return renderTimeSlot(startTime);
				})}
			</List>
		);
	}
	if (!showTimesOutsideOpeningHours) {
		return (
			<List dense className={classes.startTimesList} subheader={<li />}>
				{startTimesByType.startTimesWithinHours.map((startTime) => {
					return renderTimeSlot(startTime);
				})}
			</List>
		);
	}

	return (
		<List dense className={classes.startTimesList} subheader={<li />}>
			{startTimesByType.startTimesBeforeOpen.length > 0 && (
				<li className={classes.startTimesListSection}>
					<ul className={classes.startTimesListSectionUl}>
						<ListSubheader className={classes.startTimesListSectionHeader}>
							{t('timeLocationSelector.beforeShopOpens', 'Before shop opens')}
						</ListSubheader>
						{startTimesByType.startTimesBeforeOpen.map((startTime) => {
							return renderTimeSlot(startTime);
						})}
					</ul>
				</li>
			)}
			<li className={classes.startTimesListSection}>
				<ul className={classes.startTimesListSectionUl}>
					<ListSubheader className={classes.startTimesListSectionHeader}>
						{t('timeLocationSelector.withinOpeningHours', 'Within opening hours')}
					</ListSubheader>
					{startTimesByType.startTimesWithinHours.map((startTime) => {
						return renderTimeSlot(startTime);
					})}
				</ul>
			</li>
			{startTimesByType.startTimesAfterClose.length > 0 && (
				<li className={classes.startTimesListSection}>
					<ul className={classes.startTimesListSectionUl}>
						<ListSubheader className={classes.startTimesListSectionHeader}>
							{t('timeLocationSelector.afterShopCloses', 'After shop closes')}
						</ListSubheader>
						{startTimesByType.startTimesAfterClose.map((startTime) => {
							return renderTimeSlot(startTime);
						})}
					</ul>
				</li>
			)}
		</List>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		startTimesList: {
			flex: 1,
			overflow: 'auto',
		},
		startTimesListSection: {
			backgroundColor: 'inherit',
		},
		startTimesListSectionUl: {
			backgroundColor: 'inherit',
			padding: 0,
		},
		startTimesListSectionHeader: {
			background: theme.palette.background.secondary,
		},
	}),
);

export default TimeSlots;
